import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Input } from "../components/Input";
import Label from "../components/Label";
import { Link, useNavigate } from "react-router-dom";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks";
import {
  NewSignupSuccessful,
  SignupFailed,
  SignupPending,
  SignupSuccessful,
} from "../redux/slice/SignupSlice";
import { UserAPI } from "../middleware";
import { LoadingModal } from "../components/Modal";
import { Alert } from "@mui/material";
import { Inputs } from "../utils/types";
import {
  DatePicker,
  DayCalendarSkeleton,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const NewSignUp = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields, isSubmitted },
    setValue,
  } = useForm<Inputs>();

  const { isSignedIn } = useReduxSelector((state) => state.user);
  const { isLoading } = useReduxSelector((state) => state.signup);

  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<[] | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<any>(null);
  const [dateError, setDateError] = React.useState<string | null>(null);
  const [dateFieldTouched, setDateFieldTouched] = React.useState(false);
  const [isLocalhost, setIsLocalhost] = React.useState(false);

  React.useEffect(() => {
    setIsLocalhost(window.location.hostname === "localhost");
  }, []);

  const fillWithDummyData = () => {
    setValue("firstName", "John");
    setValue("lastName", "Doe");
    setValue("gender", "Male");

    setValue("username", 241234567);
    setValue("email", "johndoe@example.com");
    setValue("streetAddress", "123 Test Street");
    setValue("digitalAddress", "GA-123-4567");
    setValue("regionOrState", "Greater Accra");
    setValue("city", "Accra");
    setValue("country", "Ghana");
    setValue("otherName", ""); // Add this to match the type

    const birthDate = dayjs().subtract(25, "year");
    setSelectedDate(birthDate);

    setValue("dateOfBirth", birthDate.format("YYYY-MM-DD"));
    setDateFieldTouched(true);
    setDateError(null);
  };

  React.useEffect(() => {
    register("dateOfBirth", { required: true });
  }, [register]);

  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    isSignedIn && window.location.assign("/find-a-doctor");
  }, [isSignedIn]);

  const isAtLeast18YearsOld = (birthDate: any) => {
    if (!birthDate) return false;
    const today = dayjs();
    const birth = dayjs(birthDate);
    const yearsDiff = today.diff(birth, "year");
    return yearsDiff >= 18;
  };

  const handleDateChange = (date: any) => {
    setSelectedDate(date);
    setDateFieldTouched(true);

    if (date) {
      const formattedDate = date.format("YYYY-MM-DD");
      setValue("dateOfBirth", formattedDate);

      if (!isAtLeast18YearsOld(date)) {
        setDateError("You must be at least 18 years old to sign up");
      } else {
        setDateError(null);
      }
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!selectedDate || !isAtLeast18YearsOld(selectedDate)) {
      setDateError("You must be at least 18 years old to sign up");
      return;
    }

    setOpen(true);
    dispatch(SignupPending());

    const {
      streetAddress,
      dateOfBirth,
      email,
      firstName,
      gender,
      lastName,
      username,
      otherName,
      city,
      country,
      digitalAddress,
      regionOrState,
    } = data;

    const payload = {
      address: {
        id: null,
        city: city || null,
        country: country || null,
        digitalAddress: digitalAddress || null,
        latitude: null,
        longitude: null,
        patient: null,
        postalAddress: null,
        regionOrState: regionOrState || null,
        streetAddress: streetAddress || null,
      },
      appClientToken: "8b23a10e-badc-483b-9699-32a5b1d0ec08", // or process.env.REACT_APP_CLIENT_CODE
      contacts: [
        {
          value: username || null,
          type: "mobile",
        },
        {
          value: email || null,
          type: "email",
        },
      ],
      dateOfBirth: dateOfBirth || null,
      firstName: firstName || null,
      lastName: lastName || null,
      otherName: otherName || null,
      patientNumber: null,
      signUpType: "newPatient",
      username: username || null,
      gender: gender || null,
    };

    try {
      const tokenResponse = await UserAPI.gettoken();
      const access_token = tokenResponse.data.access_token;

      console.log("onSubmit: Sending signup request with payload:", payload);

      const request: any = await UserAPI.signup(payload, access_token);
      if (request.status === 200) {
        const response = request.data;

        console.log("onSubmit: Signup response received:", response);

        if (
          response.data &&
          response.data[0] &&
          response.data[0].patientContacts
        ) {
          const mobileContact = response.data[0].patientContacts.find(
            (contact: any) =>
              contact.type === "mobile" || contact.type?.value === "mobile",
          );
          if (mobileContact && mobileContact.value) {
            localStorage.setItem("patientContact", mobileContact.value);
            console.log("Stored patient contact:", mobileContact.value);
          }
        }

        localStorage.setItem("signUpData", JSON.stringify(response));

        setOpen(false);
        dispatch(NewSignupSuccessful(response));
        navigate("/payments");
      } else {
        console.log(`onSubmit: NOT 200: ${request}`);
        console.log("Create Invoice Response:", request.errors[0].message);
        setError(request.errors[0].message);
        return request.errors[0];
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors || "Signup failed";
      dispatch(SignupFailed(errorMessage));
      setOpen(false);
      console.error("onSubmit: Error during signup:", errorMessage);
      setError(errorMessage);
    }
  };

  return (
    <>
      {!isSignedIn ? (
        <div className="py-8 md:py-0 px-8 md:px-14 h-full">
          <div className="flex w-full justify-between items-center">
            <div className="flex w-full flex-col justify-center">
              <div className="flex w-full md:w-10/12 flex-col justify-center items-center">
                <img
                  src="/assets/trust-hospital-logo.png"
                  alt="hospital logo"
                  className="w-40 cursor-pointer"
                  onClick={() => navigate("/")}
                />
                <h1 className="text-2xl font-medium mt-4">Sign Up</h1>
              </div>

              {/* Show auto-fill button only on localhost */}
              {isLocalhost && (
                <div className="w-full md:w-10/12 mt-2">
                  <button
                    type="button"
                    onClick={fillWithDummyData}
                    className="text-xs bg-primary-50 hover:opacity-90 text-white py-1 px-3 rounded-xl"
                  >
                    Fill with test data
                  </button>
                </div>
              )}

              <p className="text-xs mt-2 text-gray-700 md:w-10/12">
                New to the hospital? Kindly sign up by filling the form below
                with your correct details and submit. Thanks!
              </p>
              {error &&
                error.map((item: any) => (
                  <div key={item?.field}>
                    <Alert severity="error" className="mt-2">
                      {item?.message}
                    </Alert>
                  </div>
                ))}
              {error && (
                <div>
                  {Array.isArray(error) ? (
                    error.map((item: any, index: any) => (
                      <Alert key={index} severity="error" className="mt-2">
                        {item?.message || "An error occurred"}
                      </Alert>
                    ))
                  ) : (
                    <Alert severity="error" className="mt-2">
                      {error}
                    </Alert>
                  )}
                </div>
              )}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mt-8 w-full md:w-10/12 flex flex-col"
              >
                <div>
                  <Label label="First Name" />
                  <div className="mt-3">
                    <Input
                      autoFocus
                      type="text"
                      id="firstName"
                      autoComplete="given-name"
                      {...register("firstName", { required: true })}
                      aria-invalid={errors.firstName ? "true" : "false"}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">
                      *Your first name is required
                    </p>
                  )}
                </div>
                {/* <div className="mt-4">
                  <Label label="Other Name" />
                  <div className="mt-3">
                    <Input
                      type="text"
                      id="otherName"
                      autoComplete="given-name"
                      {...register("otherName")}
                    />
                  </div>
                </div> */}
                <div className="mt-4">
                  <Label label="Last Name" />
                  <div className="mt-3">
                    <Input
                      type="text"
                      id="lastName"
                      autoComplete="given-name"
                      {...register("lastName", { required: true })}
                      aria-invalid={errors.lastName ? "true" : "false"}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-xs text-red-500 mt-1">
                      *Your last name is required
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <Label label="Gender" />
                  <div className="mt-3 relative">
                    <select
                      id="gender"
                      className="h-10 px-2 shadow-sm focus:ring-primary-100 focus:border-primary-100 focus:outline-primary-100 block w-full sm:text-sm border border-tertiary-200 rounded-[11px] bg-white appearance-none pr-8"
                      {...register("gender", { required: true })}
                      aria-invalid={errors.gender ? "true" : "false"}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Select Gender
                      </option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  {errors.gender && (
                    <p className="text-xs text-red-500 mt-1">
                      *Your gender is required
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <Label label="Date of Birth" />
                  <div className="mt-3 w-full">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        slotProps={{
                          textField: {
                            sx: {
                              "> .MuiOutlinedInput-root": {
                                height: 45,
                                borderRadius: "12px",
                              },
                            },
                          },
                        }}
                        className="border-none shadow-none w-full"
                        disableFuture={true}
                        format="DD/MM/YYYY"
                        showDaysOutsideCurrentMonth
                        fixedWeekNumber={5}
                        renderLoading={() => <DayCalendarSkeleton />}
                        value={selectedDate}
                        onChange={handleDateChange}
                      />
                    </LocalizationProvider>
                  </div>
                  {((errors.dateOfBirth && isSubmitted) ||
                    (dateFieldTouched && !selectedDate)) &&
                    !dateError && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your date of birth is required
                      </p>
                    )}
                  {dateError && (
                    <p className="text-xs text-red-500 mt-1">{dateError}</p>
                  )}
                </div>
                <div className="mt-4">
                  <Label label="Phone Number" />
                  <div className="mt-3">
                    <Input
                      type="tel"
                      id="phone"
                      {...register("username", { required: true })}
                      aria-invalid={errors.username ? "true" : "false"}
                    />
                  </div>
                  {errors.username && (
                    <p className="text-xs text-red-500 mt-1">
                      *Your phone number is required
                    </p>
                  )}
                </div>
                <div className="mt-4 flex space-x-3 w-full items-start">
                  <div className="w-full">
                    <Label label="Street Address" />
                    <div className="mt-3 w-full">
                      <Input
                        type="text"
                        id="streetAddress"
                        {...register("streetAddress", { required: true })}
                        aria-invalid={errors.streetAddress ? "true" : "false"}
                      />
                    </div>
                    {errors.streetAddress && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your street address is required
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label label="Digital Address" />
                    <div className="mt-3 w-full">
                      <Input
                        type="text"
                        id="digitalAddress"
                        placeholder="Postal/Digital"
                        {...register("digitalAddress", { required: true })}
                        aria-invalid={errors.digitalAddress ? "true" : "false"}
                      />
                    </div>
                    {errors.digitalAddress && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your digital address is required
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex space-x-3 w-full items-start">
                  <div className="w-full">
                    <Label label="Region/State" />
                    <div className="mt-3 w-full">
                      <Input
                        type="text"
                        id="regionOrState"
                        {...register("regionOrState", { required: true })}
                        aria-invalid={errors.regionOrState ? "true" : "false"}
                      />
                    </div>
                    {errors.regionOrState && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your region/state is required
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label label="City" />
                    <div className="mt-3 w-full">
                      <Input
                        type="text"
                        id="city"
                        {...register("city", { required: true })}
                        aria-invalid={errors.city ? "true" : "false"}
                      />
                    </div>
                    {errors.city && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your city is required
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label label="Country" />
                    <div className="mt-3 w-full">
                      <Input
                        type="text"
                        id="country"
                        {...register("country", { required: true })}
                        aria-invalid={errors.country ? "true" : "false"}
                      />
                    </div>
                    {errors.country && (
                      <p className="text-xs text-red-500 mt-1">
                        *Your country is required
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <Label label="Email" />
                  <div className="mt-3">
                    <Input
                      type="text"
                      id="email"
                      aria-invalid={errors.email ? "true" : "false"}
                      {...register("email", {
                        required: "*Your email is required",
                        pattern: {
                          value:
                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                          message: "Invalid email address",
                        },
                      })}
                    />
                  </div>
                  <p className="text-xs text-red-500 mt-1">
                    {errors.email && errors.email.message}
                  </p>
                </div>
                <button className="w-full bg-primary-50 text-white mt-6 h-10 rounded-2xl font-medium text-md">
                  Sign Up
                </button>
              </form>
              <div className="md:w-10/12">
                <p className="text-xs text-center mt-6">
                  <span className="text-primary-50 underline">
                    <Link to="/signup">Sign Up</Link>
                  </span>{" "}
                  as Existing Patient
                </p>
                <p className="text-xs text-center my-4">
                  Already Have an Account?{" "}
                  <span className="text-primary-50 underline">
                    <Link to="/signin">Sign In</Link>
                  </span>
                </p>
              </div>
            </div>
            <div className="w-full flex-col hidden md:flex">
              <img src={"/assets/newuser-signup.svg"} alt="sign up illustration" />
            </div>
          </div>
          <LoadingModal
            open={open}
            handleClose={handleClose}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default NewSignUp;
